// macadamia-app/src/components/widgets/FolderGrid.tsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../../context/AuthContext';
import { apiClient } from '../../../utils/apiClient';
import { useNavigate, Link } from 'react-router-dom';
import AuthorAvatar from '../../../components/shared/AuthorAvatar';
import { getDossierViewStats } from '../../../services/dossierViewService';
import EllipsisVertical from '../../../macadamia-core/icons/EllipsisVertical';

interface IUser {
  _id: string;
  firstName: string;
  lastName: string;
  email: string;
  profilePicture: string | null;
}

interface IAuthorDetails {
  author: IUser;
  coAuthors: IUser[];
}

interface IDossier {
  _id: string;
  title: string;
  description: string;
  createdAt: string;
  updatedAt: string;
  settings: {
    visibility: string;
  };
  author: string;
  coAuthors: string[];
}

interface IInvitation {
  _id: string;
  dossierId: IDossier;
  email: string;
  status: 'pending' | 'accepted' | 'rejected';
  invitedAt: string;
  token: string;
}

interface IFavorite {
  _id: string;
  dossier: IDossier;
  favoritedAt: Date;
}

interface FolderGridProps {
  dossiersUpdated: boolean;
  setDossiersUpdated: (updated: boolean) => void;
  selectedFilter: string;
  onEditDossier?: (dossier: IDossier) => void;
  onInvitationCountChange?: (count: number) => void;
  onGrantedCountChange?: (count: number) => void;
}

interface DossierStatsState {
  totalViews: number;
  uniqueViewers: number;
  activeViewers: number;
  isLoading: boolean;
  hasAttempted: boolean;
}

interface AuthorLoadingState {
  isLoading: boolean;
  hasAttempted: boolean;
}

const FolderGridSkeleton = () => {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
      {[...Array(6)].map((_, i) => (
        <div
          key={i}
          className="bg-mcdm-primary-200 dark:bg-mcdm-third-400 rounded-lg shadow-sm p-4 border border-transparent animate-pulse"
        >
          <div className="mb-2 flex justify-between items-center">
            <div className="h-5 bg-gray-300 dark:bg-gray-600 rounded-full w-16"></div>
            {i % 2 === 0 && (
              <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded w-12"></div>
            )}
          </div>

          <div className="h-6 bg-gray-300 dark:bg-gray-600 rounded mb-2 w-3/4"></div>

          <div className="space-y-2 mb-3">
            <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded w-full"></div>
            <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded w-2/3"></div>
          </div>

          <div className="h-3 bg-gray-300 dark:bg-gray-600 rounded mb-3 w-4/5"></div>

          <div>
            <div className="flex items-center mb-2">
              <div className="w-6 h-6 bg-gray-300 dark:bg-gray-600 rounded-full mr-2"></div>
              <div className="h-3 bg-gray-300 dark:bg-gray-600 rounded w-32"></div>
            </div>

            {i % 3 === 0 && (
              <div className="flex items-center">
                <div className="h-3 bg-gray-300 dark:bg-gray-600 rounded w-16 mr-2"></div>
                <div className="flex -space-x-2">
                  <div className="w-6 h-6 bg-gray-300 dark:bg-gray-600 rounded-full border-2 border-white dark:border-mcdm-third-400"></div>
                  <div className="w-6 h-6 bg-gray-300 dark:bg-gray-600 rounded-full border-2 border-white dark:border-mcdm-third-400"></div>
                </div>
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

const FolderGrid: React.FC<FolderGridProps> = ({
  dossiersUpdated,
  setDossiersUpdated,
  selectedFilter,
  onEditDossier,
  onInvitationCountChange,
  onGrantedCountChange
}) => {
  const [dossiers, setDossiers] = useState<IDossier[]>([]);
  const [favoriteDossiers, setFavoriteDossiers] = useState<IDossier[]>([]);
  const [grantedDossiers, setGrantedDossiers] = useState<IDossier[]>([]);
  const [pendingInvitations, setPendingInvitations] = useState<IInvitation[]>([]);
  const [authorDetails, setAuthorDetails] = useState<Record<string, IAuthorDetails>>({});
  const [isReady, setIsReady] = useState<boolean>(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [deleteConfirmation, setDeleteConfirmation] = useState<{ dossierId: string; title: string } | null>(null);

  const [authorLoadingStates, setAuthorLoadingStates] = useState<Record<string, AuthorLoadingState>>({});

  const [dossierStats, setDossierStats] = useState<Record<string, DossierStatsState>>({});
  const [statsLoadingQueue, setStatsLoadingQueue] = useState<string[]>([]);

  const { user } = useAuth();
  const navigate = useNavigate();

  const lastFetchTime = useRef<number>(0);
  const isCurrentlyFetching = useRef<boolean>(false);
  const dropdownRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const statsTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const authorTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const statsProcessingRef = useRef<boolean>(false);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (openDropdown && dropdownRefs.current[openDropdown]) {
        const dropdownElement = dropdownRefs.current[openDropdown];
        if (dropdownElement && !dropdownElement.contains(event.target as Node)) {
          setOpenDropdown(null);
        }
      }
    };

    if (openDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [openDropdown]);

  // NOUVELLE FONCTION BATCH pour récupérer tous les auteurs en une seule requête
  const fetchBatchAuthors = useCallback(async (dossierIds: string[]) => {
    if (dossierIds.length === 0) return;

    // Marquer tous comme "en chargement"
    const newLoadingStates: Record<string, AuthorLoadingState> = {};
    dossierIds.forEach(id => {
      newLoadingStates[id] = {
        isLoading: true,
        hasAttempted: true
      };
    });
    setAuthorLoadingStates(prev => ({
      ...prev,
      ...newLoadingStates
    }));

    try {
      const response = await apiClient('/dossiers/batch-authors', {
        method: 'POST',
        body: JSON.stringify({ dossierIds })
      });

      if (response.status === 401) {
        return;
      }

      if (!response.ok) {
        throw new Error('Erreur lors de la récupération batch des auteurs');
      }

      const data = await response.json();

      // Mettre à jour tous les auteurs en une seule fois
      setAuthorDetails(prev => ({
        ...prev,
        ...data
      }));

      // Marquer tous comme "chargés"
      const loadedStates: Record<string, AuthorLoadingState> = {};
      dossierIds.forEach(id => {
        loadedStates[id] = {
          isLoading: false,
          hasAttempted: true
        };
      });

      setAuthorLoadingStates(prev => ({
        ...prev,
        ...loadedStates
      }));

    } catch (error) {
      console.error('Erreur récupération batch auteurs:', error);

      // Marquer tous comme "échoués"
      const failedStates: Record<string, AuthorLoadingState> = {};
      dossierIds.forEach(id => {
        failedStates[id] = {
          isLoading: false,
          hasAttempted: true
        };
      });

      setAuthorLoadingStates(prev => ({
        ...prev,
        ...failedStates
      }));
    }
  }, []);

  const fetchDossierStats = useCallback(async (dossierId: string) => {
    setDossierStats(prev => {
      const currentStats = prev[dossierId];
      if (currentStats?.isLoading || currentStats?.hasAttempted) {
        return prev;
      }

      const newStats = {
        ...prev,
        [dossierId]: {
          totalViews: 0,
          uniqueViewers: 0,
          activeViewers: 0,
          isLoading: true,
          hasAttempted: true
        }
      };

      (async () => {
        try {
          const stats = await getDossierViewStats(dossierId, 'month');

          setDossierStats(current => ({
            ...current,
            [dossierId]: {
              totalViews: stats?.totalViews || 0,
              uniqueViewers: stats?.uniqueViewers || 0,
              activeViewers: stats?.activeViewers || 0,
              isLoading: false,
              hasAttempted: true
            }
          }));

        } catch (error) {
          console.error(`Erreur stats pour ${dossierId}:`, error);
          setDossierStats(current => ({
            ...current,
            [dossierId]: {
              totalViews: 0,
              uniqueViewers: 0,
              activeViewers: 0,
              isLoading: false,
              hasAttempted: true
            }
          }));
        }
      })();

      return newStats;
    });
  }, []);

  const processStatsQueue = useCallback(async () => {
    if (statsProcessingRef.current || statsLoadingQueue.length === 0) {
      return;
    }

    statsProcessingRef.current = true;

    try {
      const chunks = [];
      for (let i = 0; i < statsLoadingQueue.length; i += 3) {
        chunks.push(statsLoadingQueue.slice(i, i + 3));
      }

      for (const chunk of chunks) {
        await Promise.all(chunk.map(dossierId => fetchDossierStats(dossierId)));

        if (chunks.length > 1) {
          await new Promise(resolve => setTimeout(resolve, 300));
        }
      }

      setStatsLoadingQueue([]);
    } finally {
      statsProcessingRef.current = false;
    }
  }, [statsLoadingQueue, fetchDossierStats]);

  useEffect(() => {
    if (statsLoadingQueue.length > 0 && !statsProcessingRef.current) {
      const timeoutId = setTimeout(() => {
        processStatsQueue();
      }, 500);

      return () => clearTimeout(timeoutId);
    }
  }, [statsLoadingQueue.length, processStatsQueue]);

  const fetchPendingInvitations = useCallback(async () => {
    try {
      const response = await apiClient('/invitations/user/pending');

      if (!response.ok) throw new Error('Erreur lors de la récupération des invitations');

      const invitations = await response.json();
      setPendingInvitations(invitations);

      // Notifier le composant parent du nombre d'invitations
      if (onInvitationCountChange) {
        onInvitationCountChange(invitations.length);
      }
    } catch (error) {
      console.error('Erreur lors de la récupération des invitations:', error);
      setPendingInvitations([]);

      // Notifier le composant parent qu'il n'y a pas d'invitations
      if (onInvitationCountChange) {
        onInvitationCountChange(0);
      }
    }
  }, [onInvitationCountChange]);

  const fetchGrantedDossiers = useCallback(async () => {
    try {
      const response = await apiClient('/access-requests/my/granted');

      if (!response.ok) throw new Error('Erreur lors de la récupération des accès accordés');

      const granted = await response.json();
      setGrantedDossiers(granted);

      if (onGrantedCountChange) {
        onGrantedCountChange(granted.length);
      }
    } catch (error) {
      console.error('Erreur lors de la récupération des accès accordés:', error);
      setGrantedDossiers([]);

      if (onGrantedCountChange) {
        onGrantedCountChange(0);
      }
    }
  }, [onGrantedCountChange]);

  const fetchDossiers = useCallback(async (force = false) => {
    if (!force && isCurrentlyFetching.current) {
      return;
    }

    const now = Date.now();
    if (!force && now - lastFetchTime.current < 5000) {
      return;
    }

    isCurrentlyFetching.current = true;
    lastFetchTime.current = now;

    try {
      const [dossiersResponse, favoritesResponse] = await Promise.all([
        apiClient('/dossiers/user'),
        apiClient('/favorites/')
      ]);

      await Promise.all([fetchPendingInvitations(), fetchGrantedDossiers()]);

      if (!dossiersResponse.ok || !favoritesResponse.ok) {
        if (dossiersResponse.status === 401 || favoritesResponse.status === 401) {
          navigate('/login');
          return;
        }
        throw new Error('Erreur réseau');
      }

      const [dossiersData, favoritesData] = await Promise.all([
        dossiersResponse.json(),
        favoritesResponse.json()
      ]);

      setDossiers(dossiersData);
      setFavoriteDossiers(favoritesData.map((fav: IFavorite) => fav.dossier));
      setDossiersUpdated(false);
      setIsReady(true);

      const allDossiers = [...dossiersData, ...favoritesData.map((fav: IFavorite) => fav.dossier)];
      const uniqueDossierIds = Array.from(new Set(allDossiers.map(d => d._id)));

      if (authorTimeoutRef.current) {
        clearTimeout(authorTimeoutRef.current);
      }

      // MODIFICATION CRITIQUE : Utiliser la nouvelle fonction batch au lieu de la queue
      authorTimeoutRef.current = setTimeout(() => {
        fetchBatchAuthors(uniqueDossierIds); // ← 1 SEUL APPEL pour tous les auteurs
      }, 300);

      const publicDossiers = allDossiers.filter((d: IDossier) => {
        if (d.settings.visibility !== 'public') return false;
        const isAuthor = user?.id === d.author;
        const isCoAuthor = d.coAuthors.includes(user?.id || '');
        const isAdmin = user?.role === 'admin';
        return isAuthor || isCoAuthor || isAdmin;
      });

      if (statsTimeoutRef.current) {
        clearTimeout(statsTimeoutRef.current);
      }

      statsTimeoutRef.current = setTimeout(() => {
        const uniquePublicIds = Array.from(new Set(publicDossiers.map(d => d._id)));
        setStatsLoadingQueue(uniquePublicIds);
      }, 1000);

    } catch (error) {
      console.error('Erreur:', error);
      setIsReady(true);
    } finally {
      isCurrentlyFetching.current = false;
    }
  }, [navigate, setDossiersUpdated, fetchPendingInvitations, fetchGrantedDossiers, fetchBatchAuthors]);

  useEffect(() => {
    fetchDossiers(true);
  }, [dossiersUpdated]);

  useEffect(() => {
    return () => {
      if (statsTimeoutRef.current) {
        clearTimeout(statsTimeoutRef.current);
      }
      if (authorTimeoutRef.current) {
        clearTimeout(authorTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        const now = Date.now();
        if (now - lastFetchTime.current > 2 * 60 * 1000) {
          fetchDossiers();
        }
      }
    };

    window.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      window.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [fetchDossiers]);

  const getAuthorState = (dossierId: string): { authors: IAuthorDetails | null; isLoading: boolean; hasData: boolean } => {
    const authors = authorDetails[dossierId];
    const loadingState = authorLoadingStates[dossierId];

    return {
      authors: authors || null,
      isLoading: loadingState?.isLoading || false,
      hasData: !!authors
    };
  };

  const getRealViews = (dossierId: string): { count: number; isLoading: boolean; hasData: boolean } => {
    const stats = dossierStats[dossierId];

    if (!stats) {
      return { count: 0, isLoading: false, hasData: false };
    }

    return {
      count: stats.totalViews,
      isLoading: stats.isLoading,
      hasData: stats.hasAttempted && !stats.isLoading
    };
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const getVisibilityBadge = (visibility: string) => {
    const baseClasses = "text-xs px-2 py-1 rounded-full font-medium";

    switch (visibility) {
      case 'public':
        return (
          <span className={`${baseClasses} bg-mcdm-green-300 text-white`}>
            Public
          </span>
        );
      case 'private':
        return (
          <span className={`${baseClasses} bg-mcdm-secondary-300 text-white`}>
            Privé
          </span>
        );
      default:
        return (
          <span className={`${baseClasses} bg-mcdm-primary-400 text-white`}>
            Sur demande
          </span>
        );
    }
  };

  const getInvitationBadge = () => {
    const baseClasses = "text-xs px-2 py-1 rounded-full font-medium";
    return (
      <span className={`${baseClasses} bg-orange-400 text-white`}>
        Invitation en attente
      </span>
    );
  };

  const formatViews = (count: number): string => {
    if (count >= 1000) {
      return `${(count / 1000).toFixed(1)}k`;
    }
    return count.toString();
  };

  const truncateDescription = (description: string, maxLength: number) => {
    if (description.length <= maxLength) return description;
    return `${description.substring(0, maxLength)}(...)`;
  };

  const handleMenuAction = (action: string, dossierId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setOpenDropdown(null);

    const dossier = [...dossiers, ...favoriteDossiers].find(d => d._id === dossierId);

    switch (action) {
      case 'rename':
        if (dossier && onEditDossier) {
          onEditDossier(dossier);
        }
        break;
      case 'delete':
        if (dossier) {
          setDeleteConfirmation({ dossierId: dossier._id, title: dossier.title });
        }
        break;
    }
  };

  const [isDeleting, setIsDeleting] = useState(false);

  const handleDeleteDossier = async () => {
    if (!deleteConfirmation || isDeleting) return;

    setIsDeleting(true);
    try {
      const response = await apiClient(`/dossiers/${deleteConfirmation.dossierId}`, {
        method: 'DELETE',
      });

      if (response.status === 401) {
        navigate('/login');
        return;
      }

      // DELETE est idempotent côté REST : 200 (succès) et 404 (déjà supprimé)
      // sont tous les deux des succès du point de vue de l'utilisateur.
      if (!response.ok && response.status !== 404) {
        throw new Error('Erreur lors de la suppression du dossier');
      }

      setDeleteConfirmation(null);
      setDossiersUpdated(true);
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
      alert('Erreur lors de la suppression du dossier. Veuillez réessayer.');
    } finally {
      setIsDeleting(false);
    }
  };

  const toggleDropdown = (dossierId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setOpenDropdown(openDropdown === dossierId ? null : dossierId);
  };

  const renderDossierCard = (dossier: IDossier) => {
    const isPublic = dossier.settings.visibility === 'public';
    const { authors, isLoading: authorsLoading, hasData: hasAuthorData } = getAuthorState(dossier._id);
    const isAuthor = user?.id === authors?.author._id;
    const { count: viewCount, isLoading: viewsLoading, hasData: hasViewData } = getRealViews(dossier._id);

    return (
      <div
        key={dossier._id}
        className="bg-mcdm-primary-200 dark:bg-mcdm-third-400 rounded-lg shadow-sm hover:shadow-md 
                   transition-all duration-300 p-4 border border-transparent 
                   hover:border-mcdm-secondary-300 dark:hover:border-mcdm-secondary-400"
      >
        <div className="mb-2 flex justify-between items-center">
          {getVisibilityBadge(dossier.settings.visibility)}
          <div className="flex items-center gap-2">
            {isPublic && (
              <div className="flex items-center text-xs text-mcdm-third-400 dark:text-mcdm-primary-200">
                <svg
                  className="w-3 h-3 mr-1"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                  <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" />
                </svg>
                {viewsLoading ? (
                  <div className="w-4 h-3 bg-gray-300 dark:bg-gray-600 rounded animate-pulse"></div>
                ) : hasViewData ? (
                  <span title={`${viewCount} vues au total`}>
                    {formatViews(viewCount)}
                  </span>
                ) : (
                  <span className="opacity-50">-</span>
                )}
              </div>
            )}

            <div className="relative" ref={(ref) => dropdownRefs.current[dossier._id] = ref}>
              <button
                onClick={(e) => toggleDropdown(dossier._id, e)}
                className="flex items-center justify-center w-8 h-8 text-mcdm-third-400 dark:text-mcdm-primary-200 hover:text-mcdm-third-500 dark:hover:text-mcdm-primary-100 transition-colors z-10 relative"
              >
                <EllipsisVertical className="w-6 h-6" />
              </button>

              <AnimatePresence>
                {openDropdown === dossier._id && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95, y: -10 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.95, y: -10 }}
                    transition={{ duration: 0.2 }}
                    className="absolute top-full right-0 w-36 bg-white dark:bg-mcdm-third-300 rounded-lg shadow-lg border border-gray-200 dark:border-mcdm-third-200 overflow-hidden z-50"
                  >
                    <div className="py-1">
                      <motion.button
                        whileHover={{ backgroundColor: "rgba(0,0,0,0.05)" }}
                        whileTap={{ scale: 0.98 }}
                        onClick={(e) => handleMenuAction('rename', dossier._id, e)}
                        className="w-full px-3 py-2 text-left text-sm text-mcdm-third-500 dark:text-mcdm-primary-100 hover:bg-gray-50 dark:hover:bg-mcdm-third-200 transition-colors"
                      >
                        Renommer
                      </motion.button>

                      <motion.button
                        whileHover={{ backgroundColor: "rgba(0,0,0,0.05)" }}
                        whileTap={{ scale: 0.98 }}
                        onClick={(e) => handleMenuAction('delete', dossier._id, e)}
                        className="w-full px-3 py-2 text-left text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                      >
                        Supprimer
                      </motion.button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>

        <Link
          to={`/dossiers/${dossier._id}`}
          className="block mb-2 group"
        >
          <h2 className="font-bold text-mcdm-third-500 dark:text-mcdm-primary-100 font-platypi group-hover:text-mcdm-secondary-400 dark:group-hover:text-mcdm-secondary-300 transition-colors cursor-pointer">
            {dossier.title}
          </h2>
        </Link>

        <p className="text-mcdm-third-400 dark:text-mcdm-primary-200 text-sm mb-3">
          {dossier.description ? (
            truncateDescription(dossier.description, 100)
          ) : (
            <span className="italic opacity-70">Sans description</span>
          )}
        </p>

        <div className="text-xs text-mcdm-third-300 dark:text-mcdm-primary-300 mb-3">
          Créé le {formatDate(dossier.createdAt)} - modifié le {formatDate(dossier.updatedAt)}
        </div>

        <div>
          {authorsLoading && !hasAuthorData ? (
            <div className="animate-pulse">
              <div className="flex items-center mb-2">
                <div className="w-6 h-6 bg-gray-300 dark:bg-gray-600 rounded-full mr-2"></div>
                <div className="h-3 bg-gray-300 dark:bg-gray-600 rounded w-32"></div>
              </div>
            </div>
          ) : authors ? (
            <>
              <div className="flex items-center">
                <div className="relative group mr-2">
                  <AuthorAvatar user={authors.author} className="" />
                  <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-black text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-20">
                    {authors.author.firstName} {authors.author.lastName}
                    <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-black"></div>
                  </div>
                </div>
                <span className="text-xs text-mcdm-third-400 dark:text-mcdm-primary-200">
                  {isAuthor ? 'Vous êtes l\'auteur' : `Par ${authors.author.firstName} ${authors.author.lastName}`}
                </span>
              </div>

              {authors.coAuthors.length > 0 && (
                <div className="mt-2 flex items-center">
                  <span className="text-xs text-mcdm-third-400 dark:text-mcdm-primary-200 mr-2">
                    Co-auteurs:
                  </span>
                  <div className="flex -space-x-2">
                    {authors.coAuthors.map((coAuthor) => (
                      <div key={coAuthor._id} className="relative group">
                        <AuthorAvatar
                          user={coAuthor}
                          className="border-2 border-white dark:border-mcdm-third-400"
                        />
                        <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-black text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-20">
                          {coAuthor.firstName} {coAuthor.lastName}
                          <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-black"></div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="text-xs text-mcdm-third-300 dark:text-mcdm-primary-300">
              Chargement des auteurs...
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderInvitationCard = (invitation: IInvitation) => {
    const dossier = invitation.dossierId;

    return (
      <Link
        key={invitation._id}
        to={`/dossiers/${invitation.dossierId._id}`}
        className="bg-mcdm-primary-200 dark:bg-mcdm-third-400 rounded-lg shadow-sm hover:shadow-md 
                   transition-all duration-300 cursor-pointer p-4 border border-orange-300 
                   hover:border-orange-400 dark:hover:border-orange-400"
      >
        <div className="mb-2">
          {getInvitationBadge()}
        </div>

        <h2 className="font-bold text-mcdm-third-500 dark:text-mcdm-primary-100 font-platypi mb-2">
          {dossier.title}
        </h2>

        <p className="text-mcdm-third-400 dark:text-mcdm-primary-200 text-sm mb-3">
          {dossier.description ? (
            truncateDescription(dossier.description, 100)
          ) : (
            <span className="italic opacity-70">Sans description</span>
          )}
        </p>

        <div className="text-xs text-mcdm-third-300 dark:text-mcdm-primary-300 mb-3">
          Invité le {formatDate(invitation.invitedAt)}
        </div>

        <div className="flex items-center">
          <span className="text-xs text-orange-600 dark:text-orange-400 font-medium">
            Cliquez pour accepter ou refuser l'invitation
          </span>
        </div>
      </Link>
    );
  };

  if (!isReady) {
    return <FolderGridSkeleton />;
  }

  const combinedDossiers = [...dossiers];
  favoriteDossiers.forEach(fav => {
    if (!combinedDossiers.some(dossier => dossier._id === fav._id)) {
      combinedDossiers.push(fav);
    }
  });
  grantedDossiers.forEach(granted => {
    if (!combinedDossiers.some(dossier => dossier._id === granted._id)) {
      combinedDossiers.push(granted);
    }
  });

  let filteredContent: Array<IDossier | IInvitation> = combinedDossiers;

  if (selectedFilter === 'Favoris') {
    filteredContent = favoriteDossiers;
  } else if (selectedFilter === 'Accès accordé') {
    filteredContent = grantedDossiers;
  } else if (selectedFilter === 'Invitations') {
    filteredContent = pendingInvitations;
  }

  filteredContent = filteredContent.sort((a, b) => {
    const dateA = 'invitedAt' in a ? new Date(a.invitedAt) : new Date(a.updatedAt);
    const dateB = 'invitedAt' in b ? new Date(b.invitedAt) : new Date(b.updatedAt);
    return dateB.getTime() - dateA.getTime();
  });

  return (
    <>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredContent.length > 0 ? (
          filteredContent.map((item) =>
            'invitedAt' in item
              ? renderInvitationCard(item as IInvitation)
              : renderDossierCard(item as IDossier)
          )
        ) : (
          <div className="col-span-full text-center text-mcdm-third-500 dark:text-mcdm-primary-200">
            {selectedFilter === 'Invitations'
              ? 'Aucune invitation en attente.'
              : selectedFilter === 'Accès accordé'
              ? 'Aucun accès accordé pour le moment.'
              : 'Aucun dossier trouvé.'
            }
          </div>
        )}
      </div>

      {/* Modal de confirmation de suppression */}
      <AnimatePresence>
        {deleteConfirmation && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setDeleteConfirmation(null)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white dark:bg-mcdm-third-400 rounded-lg shadow-xl max-w-md w-full p-6"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="text-xl font-bold text-mcdm-third-500 dark:text-mcdm-primary-100 mb-4 font-platypi">
                Confirmer la suppression
              </h3>

              <p className="text-mcdm-third-400 dark:text-mcdm-primary-200 mb-6">
                Êtes-vous sûr de vouloir supprimer le dossier <span className="font-semibold">"{deleteConfirmation.title}"</span> ?
                Cette action est irréversible et supprimera tous les contenus associés.
              </p>

              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => setDeleteConfirmation(null)}
                  className="px-4 py-2 rounded-lg bg-mcdm-primary-200 dark:bg-mcdm-third-300 text-mcdm-third-500 dark:text-mcdm-primary-100 hover:bg-mcdm-primary-300 dark:hover:bg-mcdm-third-200 transition-colors font-medium"
                >
                  Annuler
                </button>

                <button
                  onClick={handleDeleteDossier}
                  className="px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700 transition-colors font-medium"
                >
                  Supprimer
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

    </>
  );
};

export default FolderGrid;